import React, { useRef, useEffect, useMemo, useState } from 'react';
import useProviderEditDisabled from 'hooks/useProviderEditDisabled';
import { PROVIDER_FILE_TYPES } from 'services/providers.service';
import { Form, Card, Input, Radio, Button, Typography, Row, Col, Space, Spin, Divider, message } from 'antd';
import PhoneNumberContainer from 'components/PhoneNumberContainer';
import PhoneNumber from 'components/PhoneNumber';
import { getPhoneNumberValidator, validateDates, getPastDateValidator, getPhoneEXTValidator } from 'utils';
import AriaDatePicker from 'components/AriaDatePicker';
import { ADD_NAVY_HCC_STEPS } from 'constants';
import { providerService } from 'services';
import { actions as centerActions } from 'features/add-center';
import { sleep } from 'utils';
import { useDispatch } from 'react-redux';
import UploadFile from 'components/UploadFile/UploadFile';
import { useSelector } from 'react-redux';
import { selectors } from 'features/auth';
import { useAsync } from 'react-async-hook';
import { DatePickerFormItemLabel, FormItemLabel } from 'components/Form';
import commonApi from 'services/common.service';
import { Add } from '@carbon/icons-react';
import { Spacer } from 'components/Space/Space';
import { PHONE_NUMBER_EXT_CHARLIMIT } from 'constants';

const { Title, Paragraph } = Typography;

const HCCStaffMembers = ({ id, setStep, isFormSubmitting, loading, applicationSubmitted }) => {
  const [form] = Form.useForm();
  const formStaffValue = Form.useWatch('staff', form);
  const requireCprUpload = useMemo(() => {
    return formStaffValue?.some((staff) => staff?.presentOnWeekends);
  }, [formStaffValue]);

  const dispatch = useDispatch();
  const isSubmitDisabled = useProviderEditDisabled();
  const organization = useSelector(selectors.selectOrganization);
  const [applicationFiles, setApplicationFiles] = useState(null);

  const staffFiles = useMemo(
    () => applicationFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.CERTIFICATION.code),
    [applicationFiles],
  );

  // Keeps track of whether `next` is pressed or `save`
  const shouldGoToNextStep = useRef(false);

  const [fetchedStaffMembers, setFetchedStaffMembers] = useState([]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const staffMembers = await providerService.getStaffMembers(id);
        setFetchedStaffMembers(staffMembers);

        if (staffMembers?.length) {
          form.setFieldsValue({
            staff: staffMembers
              .sort((a, b) => a.id - b.id)
              .map((d) => {
                if (!d.certifications || (Array.isArray(d.certifications) && d.certifications.length === 0)) {
                  d.certifications = [{}];
                }

                return d;
              }), // Add existing staff members sorted by id
          });
        } else {
          form.setFieldsValue({
            staff: [
              {
                certifications: [{}],
              },
            ], // Add a default empty staff member
          });
        }
      } catch (error) {
        console.error('Error fetching staff data', error);
      }
    };
    fetchData();
  }, [form, id]);

  useAsync(async () => {
    if (id !== undefined) {
      try {
        const _applicationFiles = await providerService.getApplicationFiles(id);
        setApplicationFiles(_applicationFiles);
      } catch (error) {
        console.error('Error fetching organization files', error);
      }
    }
  }, [organization]);

  const [certificateTypes, setCertificateTypes] = useState(null);
  useAsync(async () => {
    const _certificateTypes = await commonApi.get('/providers/options/41', { cache: { interpretHeader: false } });
    setCertificateTypes(_certificateTypes);
  }, []);

  return (
    <div id="staff-members" data-testid="staffMembers">
      <Spin spinning={loading || certificateTypes === null}>
        <Form
          layout="vertical"
          form={form}
          scrollToFirstError
          onFinish={async (values) => {
            try {
              const { staff } = values;
              dispatch(centerActions.setIsFormSubmitting(true));

              //Add/update staff members
              if (staff.length) {
                for (const staffMember of staff) {
                  const member = await providerService.addUpdateStaffMembers(id, {
                    ...staffMember,
                    application: { id },
                  });

                  // save new ID in values
                  staffMember.id = member.id;

                  // Find out if any staff members were removed
                  const removedStaffMembers = fetchedStaffMembers?.filter(
                    (fetchedStaffMember) => !staff.some((d) => d.id === fetchedStaffMember.id),
                  );
                  // Delete the removed staff members
                  if (removedStaffMembers?.length) {
                    for (const removedStaffMember of removedStaffMembers) {
                      // also delete the certifications
                      for (const cert of removedStaffMember.certifications) {
                        if (cert.id !== undefined) {
                          await providerService.deleteStaffMemberCert(id, removedStaffMember.id, cert.id);
                        }
                      }

                      await providerService.deleteStaffMember(id, removedStaffMember.id);
                    }
                  }

                  if (
                    Array.isArray(staffMember.certifications) &&
                    member &&
                    staffMember.certifications.some((d) => !!d?.instructor)
                  ) {
                    for (const _certificate of staffMember.certifications) {
                      const _newOrUpdatedCertificate = await providerService.addUpdateStaffMemberCert(
                        id,
                        member.id,
                        _certificate,
                      );

                      // save new ID in values
                      _certificate.id = _newOrUpdatedCertificate.id;
                    }

                    // Find out if any certifications were removed
                    const removedCertifications =
                      staffMember.presentOnWeekends === false
                        ? staffMember.certifications
                        : fetchedStaffMembers
                            ?.find((fetchedStaffMember) => fetchedStaffMember.id === staffMember.id)
                            ?.certifications.filter(
                              (fetchedCert) => !staffMember.certifications?.some((cert) => cert.id === fetchedCert.id),
                            );

                    if (removedCertifications?.length) {
                      for (const removedCert of removedCertifications) {
                        if (removedCert.id !== undefined) {
                          await providerService.deleteStaffMemberCert(id, member.id, removedCert.id);

                          // remove it also from values
                          staffMember.certifications = staffMember.certifications.filter(
                            (d) => d.id !== removedCert.id,
                          );
                          if (staffMember.certifications.length === 0) {
                            staffMember.certifications = [{}];
                          }
                        }
                      }
                    }
                  }
                }

                // If none of the staff members have certificates, also delete the files
                if (!staff.some((d) => d.certifications?.length && d.certifications[0]?.id)) {
                  applicationFiles
                    ?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.CERTIFICATION.code)
                    .forEach(async (file) => {
                      await providerService.deleteApplicationFile(id, file.id);
                    });
                }

                // Save the updated staff members to the form
                form.setFieldsValue({
                  staff,
                });
              }

              if (shouldGoToNextStep.current) {
                setStep(ADD_NAVY_HCC_STEPS.RESIDENTS);
              }

              message.success('Saved');
              await sleep(100);
            } catch (err) {
              newrelic.noticeError(err);
              message.error('Unable to save center details.');
            } finally {
              dispatch(centerActions.setIsFormSubmitting(false));
            }
          }}
          disabled={applicationSubmitted}
        >
          <Title>Staff members</Title>
          <Paragraph>Provide information about all staff members working at this location.</Paragraph>
          <Form.List name="staff">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key}>
                    <StaffMember
                      index={index}
                      certificateTypes={certificateTypes}
                      allStaffMembers={fields}
                      field={field}
                      add={add}
                      remove={remove}
                      form={form}
                      id={id}
                      applicationSubmitted={applicationSubmitted}
                    />
                  </div>
                ))}
              </>
            )}
          </Form.List>

          {requireCprUpload && (
            <Card title="Upload certificates" className="mb-4">
              <Form.Item
                label={
                  <FormItemLabel
                    title="UPLOAD ALL CPR/FIRST AID CERTIFICATES"
                    subTitle="Upload a certificate for every staff member that will work weekends."
                    showAsterisk
                  />
                }
                name="staffFiles"
                rules={[
                  () => ({
                    validator() {
                      if (staffFiles.length === 0) {
                        return Promise.reject('File is required');
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
                className="mb-0 max-w-96"
                data-testid="certificate"
              >
                <UploadFile
                  fileCode={PROVIDER_FILE_TYPES.CERTIFICATION.code}
                  files={applicationFiles}
                  setFiles={setApplicationFiles}
                  applicationId={id}
                  readOnly={applicationSubmitted}
                />
              </Form.Item>
            </Card>
          )}

          <div className="flex space-x-2">
            <Button
              onClick={() => setStep(ADD_NAVY_HCC_STEPS.HOURS_OF_OPERATION)}
              style={{ borderRadius: '0px' }}
              aria-label="back button"
              disabled={false}
              data-testod="backButton"
            >
              Back
            </Button>

            <div className="flex-grow"></div>

            {!applicationSubmitted && (
              <Button
                style={{ borderRadius: '0px' }}
                htmlType="submit"
                onClick={() => {
                  shouldGoToNextStep.current = false;
                }}
                disabled={isFormSubmitting || isSubmitDisabled}
                loading={isFormSubmitting}
                aria-label="save button"
              >
                Save
              </Button>
            )}

            <Button
              style={{ borderRadius: '0px' }}
              type="primary"
              role="button"
              htmlType={!applicationSubmitted ? 'submit' : undefined}
              onClick={() => {
                if (applicationSubmitted) {
                  setStep(ADD_NAVY_HCC_STEPS.RESIDENTS);
                  return;
                }

                shouldGoToNextStep.current = true;
              }}
              disabled={isFormSubmitting || isSubmitDisabled}
              loading={isFormSubmitting}
              data-testid="staff-members-form-submit"
              aria-label="next button"
            >
              Next
            </Button>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

const StaffMember = ({
  allStaffMembers,
  certificateTypes,
  field,
  index,
  add,
  remove,
  form,
  id,
  applicationSubmitted,
}) => {
  const staffMemberArray = form.getFieldValue('staff');
  const presentOnWeekends = Form.useWatch(['staff', field.name, 'presentOnWeekends'], form);

  return (
    <div className="mb-4">
      <Card
        title={
          <span>
            <Title level={2} className="mt-4">
              Staff member {`${index + 1}`} {!!!index && '(license holder)'}
            </Title>
            <Paragraph style={{ fontWeight: 'normal' }}>
              This person’s name should be on your business’s child care license.
            </Paragraph>
          </span>
        }
        extra={
          allStaffMembers.length > 1 && (
            <Button
              onClick={() => {
                remove(field.name);
              }}
              style={{ borderRadius: '2px' }}
              data-testid="removeButton"
            >
              Remove staff member {index + 1}
            </Button>
          )
        }
        data-testid="staffMemberCard"
      >
        <Row gutter={[20, 0]} className="form-row">
          <Col xs={24} sm={22} md={12}>
            <Form.Item
              label="first name"
              name={[field.name, 'person', 'firstName']}
              rules={[{ required: true, message: 'First name is required' }]}
              data-testid="firstName"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={22} md={12}>
            <Form.Item
              label="last name"
              name={[field.name, 'person', 'lastName']}
              rules={[{ required: true, message: 'Last name is required' }]}
              data-testid="lastName"
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Col xs={18} sm={16} md={6}>
          <Form.Item
            label={<DatePickerFormItemLabel title="Date of birth" />}
            name={[field.name, 'person', 'dateOfBirth']}
            rules={[
              {
                required: true,
                message: 'Date of birth is required',
              },
              getPastDateValidator('Date of birth should be in the past.'),
            ]}
          >
            <AriaDatePicker disabled={applicationSubmitted} data-testid="dateOfBirth" />
          </Form.Item>
          <Form.Item
            label="role"
            name={[field.name, 'person', 'position']}
            rules={[{ required: true, message: 'Role is required' }]}
            data-testid="role"
          >
            <Input />
          </Form.Item>
        </Col>
        <Form.Item
          label={
            <FormItemLabel
              title="will this staff member be present at the child care location on weekends?"
              subTitle="Choose “Yes” if this staff member will be present anytime between 6:00am and 6:00pm on Saturdays or
                Sundays."
            />
          }
          name={[field.name, 'presentOnWeekends']}
          rules={[{ required: true, message: 'Selection is required' }]}
          data-testid="presentOnWeekends"
        >
          <Radio.Group data-testid="radioGroup">
            <Space direction="vertical">
              <Radio value={true} data-testid="yesRadio">
                Yes
              </Radio>
              <Radio value={false} data-testid="noRadio">
                No
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {presentOnWeekends && (
          <Form.List name={[index, 'certifications']}>
            {(fields, { add, remove }) => {
              return (
                <span data-testid="CPRandFirstAidSection">
                  <Title level={3}>First aid and CPR certification</Title>

                  {fields.map((field, certificateIndex) => (
                    <>
                      {certificateIndex !== 0 && <Divider />}

                      <div className="block md:flex gap-4">
                        <div>
                          <Title level={4}>First aid/CPR certification {certificateIndex + 1}</Title>

                          <Paragraph>
                            All staff who will be at the child care location on weekends must possess an active
                            infant/pediatric first aid and CPR certification.
                          </Paragraph>
                        </div>

                        <Spacer />

                        {fields.length > 1 && (
                          <Button
                            onClick={() => {
                              remove(field.name);
                            }}
                            style={{ borderRadius: '2px' }}
                          >
                            Remove certification {certificateIndex + 1}
                          </Button>
                        )}
                      </div>

                      <Form.Item
                        name={[field.name, 'certificationType', 'id']}
                        label="CERTIFICATION TYPE"
                        rules={[
                          {
                            required: true,
                            message: 'This is required',
                          },
                        ]}
                      >
                        <Radio.Group disabled={applicationSubmitted}>
                          <Space direction="vertical">
                            {certificateTypes?.map((cert) => (
                              <Radio key={cert.id} value={cert.id} data-testid={`${cert.title}Radio`}>
                                {cert.title}
                              </Radio>
                            ))}
                          </Space>
                        </Radio.Group>
                      </Form.Item>
                      <Col xs={18} sm={16} md={7}>
                        <Form.Item
                          label="certificate number"
                          name={[field.name, 'number']}
                          rules={[{ required: true, message: 'Certificate number is required' }]}
                          data-testid="certificateNumber"
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          label={<DatePickerFormItemLabel title="certification issue date" />}
                          name={[field.name, 'issueDate']}
                          rules={[
                            {
                              required: true,
                              message: 'certification issue date is required',
                            },
                            {
                              validator: validateDates(
                                form.getFieldValue,
                                [field.name, 'issueDate'],
                                [field.name, 'expirationDate'],
                                'Issue date must be before expiration date',
                              ),
                            },
                          ]}
                          data-testid="certIssueDate"
                        >
                          <AriaDatePicker disabled={applicationSubmitted} />
                        </Form.Item>
                        <Form.Item
                          label={<DatePickerFormItemLabel title="certification expiration date" />}
                          name={[field.name, 'expirationDate']}
                          rules={[
                            { required: true, message: 'certification expiration date is required' },
                            {
                              validator: validateDates(
                                form.getFieldValue,
                                [field.name, 'issueDate'],
                                [field.name, 'expirationDate'],
                                'Expiration date must be after issue date',
                              ),
                            },
                          ]}
                          data-testid="certExpirationDate"
                        >
                          <AriaDatePicker disabled={applicationSubmitted} />
                        </Form.Item>
                      </Col>
                      <Row gutter={[20, 0]} className="form-row">
                        <Col xs={24} sm={22} md={12}>
                          <Form.Item
                            label="instructor first name"
                            name={[field.name, 'instructor', 'firstName']}
                            rules={[{ required: true, message: 'Instructor first name is required' }]}
                            data-testid="instructorFirstName"
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={22} md={12}>
                          <Form.Item
                            label="instructor last name"
                            name={[field.name, 'instructor', 'lastName']}
                            rules={[{ required: true, message: 'Instructor last name is required' }]}
                            data-testid="instructorLastName"
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[20, 0]} className="form-row">
                        <Col xs={24} sm={22} md={12}>
                          <Form.Item
                            label="Instructor email address"
                            name={[field.name, 'instructor', 'email']}
                            rules={[{ required: true, message: 'Instructor email address is required' }]}
                            data-testid="instructorEmailAddress"
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row>
                        <PhoneNumberContainer
                          phoneNumber={
                            <Form.Item
                              name={[field.name, 'instructor', 'phone']}
                              label="instructor phone number"
                              rules={[
                                { required: true, message: 'Instructor phone number is required.' },
                                getPhoneNumberValidator('Invalid Phone Number'),
                              ]}
                              className="mb-0"
                            >
                              <PhoneNumber
                                aria-label="instructor phone number"
                                disabled={applicationSubmitted}
                                data-testid="instructorPhoneNumber"
                              />
                            </Form.Item>
                          }
                          extension={
                            <Form.Item
                              name={[field.name, 'instructor', 'phoneExt']}
                              label="extension"
                              rules={[getPhoneEXTValidator('Invalid Extension.')]}
                              data-testid="extension"
                              className="mb-0"
                            >
                              <Input maxLength={PHONE_NUMBER_EXT_CHARLIMIT} />
                            </Form.Item>
                          }
                        />
                      </Row>
                      {certificateIndex === fields.length - 1 && (
                        <Button
                          onClick={() => {
                            add({});
                          }}
                          icon={<Add />}
                          type="primary"
                          className="mt-4"
                          style={{ borderRadius: '2px' }}
                          data-testid="addButton"
                        >
                          Add another certificate for staff member {`${index + 1}`}
                        </Button>
                      )}
                    </>
                  ))}
                </span>
              );
            }}
          </Form.List>
        )}
      </Card>

      {index + 1 === staffMemberArray.length && (
        <Card className="mt-4">
          <Button
            onClick={() => {
              add({
                certifications: [{}],
              });
            }}
            icon={<Add />}
            type="primary"
            style={{ borderRadius: '2px' }}
            data-testid="addButton"
          >
            Add another staff member
          </Button>
        </Card>
      )}
    </div>
  );
};

export default HCCStaffMembers;
